<template>
  <div class="registration-form__step registration-form__step-contact-data">
    <div class="registration-form__step__container">
      <div
        v-if="salutationList.length"
        class="salutation-list"
      >
        <label
          v-if="fields.salutationLabel"
          class="required"
        >
          {{ fields.salutationLabel.value }}
        </label>
        <radio-group-fi
          v-model="salutation"
          name="salutation"
          text-attribute="title"
          data-test="salutation-list"
          :data-test-2="salutation"
          :options="salutationListWithUnset"
          required
        />
      </div>

      <div class="data">
        <div
          v-if="titleList.length"
          class="title-list"
        >
          <select-input-fi
            :value="title || null"
            :label="fields.academicTitleLabel?.value"
            :options="titleList"
            data-test="title"
            :data-test-2="title"
            @input="titleChanged"
          />
        </div>

        <text-input-fi
          ref="firstname"
          v-model.trim="firstname"
          class="firstname"
          name="firstname"
          data-test="firstname"
          :rules="rules.firstname"
          :maxlength="120"
          :label="fields.firstNameLabel?.value"
          required
        />

        <text-input-fi
          ref="lastname"
          v-model.trim="lastname"
          name="lastname"
          data-test="lastname"
          :rules="rules.lastname"
          :maxlength="120"
          :label="fields.lastNameLabel?.value"
          required
        />

        <text-input-fi
          ref="email"
          v-model.trim="email"
          name="email"
          data-test="email"
          :rules="
            hasInvalidCharacters || enableEmailValidation
              ? rules.email
              : dynamicRule(rules.email, enableEmailValidationDefault)
          "
          :maxlength="120"
          :label="fields.emailLabel?.value"
          :disabled="registrationLight"
          :required="!registrationLight"
          @keyup="validateCharacters(email)"
          @keydown="enableEmailValidationDefault = false"
          @blur="emailBlurred"
        />

        <text-input-fi
          id="phone"
          ref="phone"
          v-model.trim="phone"
          name="phone"
          data-test="phone"
          :rules="hasInvalidCharacters || enablePhoneValidation ? rules.phone : []"
          :maxlength="120"
          :label="fields.phoneLabel?.value"
          @keyup="validateCharacters(phone, rules.phone)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { RadioGroupFi, SelectInputFi, TextInputFi } from 'atoms';
import { makeSalutaionListWithUnset } from 'composables/makeSalutationListWithUnset';
import validatorsMixin from 'mixins/validatorsMixin';

import { getSelectList } from '@/utils/fieldTransformations';

export default {
  name: 'RegistrationStepContactData',
  components: {
    RadioGroupFi,
    SelectInputFi,
    TextInputFi,
  },
  mixins: [validatorsMixin],
  inject: ['fields'],
  emits: ['valid-changed'],
  data() {
    return {
      isValid: false,
      activeTitle: null,
      enableEmailValidation: false,
      enableEmailValidationDefault: true,
      enablePhoneValidation: false,
    };
  },
  computed: {
    registrationLight() {
      return this.fields.registrationType?.value === 'Light';
    },
    titleList() {
      const [, titleList] = getSelectList(this.fields, 'titleList');
      return titleList || [];
    },
    salutationListWithUnset() {
      return makeSalutaionListWithUnset(this.salutationList, this.$dict);
    },
    salutationList() {
      const [, salutationList] = getSelectList(this.fields, 'salutationList');
      return salutationList || [];
    },
    salutation: {
      get() {
        return this.$store.getters['registration/getSalutation'];
      },
      set(salutation) {
        this.$store.dispatch('registration/setSalutation', salutation);
        this.validate();
      },
    },
    title: {
      get() {
        return this.$store.getters['registration/getTitle'];
      },
      set(title) {
        this.$store.dispatch('registration/setTitle', title);
      },
    },
    firstname: {
      get() {
        return this.$store.getters['registration/getFirstname'];
      },
      set(firstname) {
        this.$store.dispatch('registration/setFirstname', firstname);
        this.validate();
      },
    },
    lastname: {
      get() {
        return this.$store.getters['registration/getLastname'];
      },
      set(lastname) {
        this.$store.dispatch('registration/setLastname', lastname);
        this.validate();
      },
    },
    email: {
      get() {
        return this.$store.getters['registration/getEmail'];
      },
      set(email) {
        this.$store.dispatch('registration/setEmail', email);
        this.validate();
      },
    },
    phone: {
      get() {
        return this.$store.getters['registration/getPhone'];
      },
      set(phone) {
        this.$store.dispatch('registration/setPhone', phone);
        this.validate();
      },
    },
  },
  mounted() {
    this.validate();
  },
  async created() {
    if (!this.salutationList.length) {
      this.salutation = '';
    }

    if (this.email && !this.registrationLight) {
      this.enableEmailValidation = true;
    }

    if (this.phone) {
      this.enablePhoneValidation = true;
    }
  },
  methods: {
    titleChanged(value) {
      this.title = value;
    },
    validate() {
      if (this.$refs.firstname && this.$refs.lastname && this.$refs.email && this.$refs.phone) {
        let salutationValid = true;
        if (this.salutationList.length) {
          salutationValid = this.salutation !== null;
        }

        this.isValid = Boolean(
          salutationValid &&
            this.firstname !== '' &&
            !this.$refs.firstname.errorBucket.length &&
            this.lastname !== '' &&
            !this.$refs.lastname.errorBucket.length &&
            (this.registrationLight ||
              (this.email !== '' &&
                !this.$refs.email.errorBucket.length &&
                this.enableEmailValidation)) &&
            !this.$refs.phone.errorBucket.length,
        );
      } else {
        this.isValid = false;
      }
      this.$emit('valid-changed', this.isValid);
    },
    async emailBlurred() {
      this.enableEmailValidation = true;
      await this.$nextTick();
      this.validate();
    },
  },
};
</script>

<style lang="scss">
@import './registration-step-contact-data-unscoped';
</style>

<style lang="scss" scoped>
@import './registration-step-contact-data';
</style>
